import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// actions
import * as customerSpdActionCreators from '../actions/CustomerSpdActionCreators';

// interfaces/types
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import * as customerSpdSelectors from '../selectors/customerSpdSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { SAVE_OFFER, UPDATE_DEAL_REF_ID } from '../actionTypes/offerActionTypes';
import type { CustomerSpd } from '@makemydeal/dr-dash-types';

// utils
import { hasDataChanged } from '../utils/customerSpdUtils';
import { CUSTOMER_SPD_SUCCESS, CUSTOMER_SPD_FAILURE } from '../actionTypes/CustomerSpdTypes';
import { runContractValidationFlow } from '../actions/contractingActionCreators';
import { isContractValidationLoading } from '../selectors/contractingSelectors';
import * as offerSelectors from '../selectors/offerSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { AIO_DXG_EVENTS } from '../actionTypes/socketActionTypes';

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);
    const state = store.getState();

    const performCustomerSpdSaveIfChanged = () => {
        const currentState = store.getState();
        const isCustomerSPDEnabled = dealerSelectors.isSpdManagementEnabled(currentState);
        const isUpdateCustomerLoading = customerSpdSelectors.getSaveCustomerSpdLoading()(currentState);
        const isActivitiesCustomerCardEnabled = featureToggleSelectors.isActivitiesCustomerCardEnabled(currentState);

        // Skip if an update is already in progress, SPD management is disabled, or the Activities Customer Card FT is on
        if (!isCustomerSPDEnabled || isUpdateCustomerLoading || isActivitiesCustomerCardEnabled) {
            return;
        }

        const participantsSpd = customerSpdSelectors.getAllCustomersSpdData(currentState);
        const customerCommonConsumerIds = Object.keys(participantsSpd);

        const updateCustomerSpdData: CustomerSpd[] = customerCommonConsumerIds
            .map((commonConsumerId) => {
                const { bankAccountNumber: _omitBankAccountNumber, ...customerSpdData } = participantsSpd[commonConsumerId];

                const getCustomerResponse = customerSpdSelectors.getCustomerSpdResult(commonConsumerId)(currentState);
                const checkCustomerResponse = customerSpdSelectors.getCheckCustomerSpdResult(commonConsumerId)(currentState);

                const driversLicenseState = getCustomerResponse?.driversLicenseState || checkCustomerResponse?.driversLicenseState;

                const previousSpdData =
                    getCustomerResponse || driversLicenseState
                        ? { ...getCustomerResponse, driversLicenseState, bankAccountNumber: undefined }
                        : undefined;

                const hasChanges = hasDataChanged(customerSpdData, previousSpdData);

                return hasChanges
                    ? {
                          commonConsumerId,
                          ...customerSpdData
                      }
                    : null;
            })
            .filter(Boolean) as CustomerSpd[];

        if (updateCustomerSpdData.length > 0) {
            store.dispatch(customerSpdActionCreators.updateCustomerSpdRSAA(updateCustomerSpdData));
        }
    };
    switch (action.type) {
        case SAVE_OFFER: {
            // Trigger SPD save only if offer already has a dealRefId prior to save
            const existingDealRefId = offerSelectors.getDealRefId(state);
            if (existingDealRefId) {
                performCustomerSpdSaveIfChanged();
            }
            break;
        }

        case AIO_DXG_EVENTS: {
            const event = action.payload;
            const dealRefId = offerSelectors.getDealRefId(state);
            if (!dealRefId && event?.type === 'fsFailure') {
                performCustomerSpdSaveIfChanged();
            }
            break;
        }

        case UPDATE_DEAL_REF_ID: {
            // Perform SPD save if a dealrefId is modified
            performCustomerSpdSaveIfChanged();
            break;
        }

        case CUSTOMER_SPD_SUCCESS:
        case CUSTOMER_SPD_FAILURE: {
            const currentState = store.getState();

            if (!isContractValidationLoading(currentState)) {
                break;
            }

            const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(currentState);
            const coBuyerCommonConsumerId = offerReduxSelectors.getCoBuyerCommonConsumerId(currentState);

            const spdResponses = currentState.customerSpd.getCustomerResponse;
            const spdErrors = currentState.customerSpd.getCustomerError;

            const allKeys = new Set([...Object.keys(spdResponses), ...Object.keys(spdErrors)]);

            // Dispatch contract validation only after the buyer SPD request completes and, if a co-buyer exists, their SPD request completes too.
            const hasBuyerResponse = allKeys.has(buyerCommonConsumerId);
            const hasCoBuyerResponseOrNotApplicable = !coBuyerCommonConsumerId || allKeys.has(coBuyerCommonConsumerId);

            if (hasBuyerResponse && hasCoBuyerResponseOrNotApplicable) {
                store.dispatch(runContractValidationFlow());
            }
            break;
        }

        default:
            break;
    }
};
